import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import AuthModal from '../components/AuthModal';
import ShareButton from '../components/ShareButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { MapPin, Droplets, UserPlus, UserCheck, Edit3, ArrowLeft, Trash2, Package, Clock, Truck as TruckIcon, CheckCircle2, ShieldCheck, RefreshCw, Heart, Bell, ShieldX, Navigation, Film, Play, Eye, EyeOff, BadgeCheck, Copy, KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AddressFormDialog from './AddressFormDialog';
import VideoPlayerModal from '../components/VideoPlayerModal';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (price) => `Rp ${Number(price).toLocaleString('id-ID')}`;

const STATUS_CONFIG = {
  pending:   { label: 'Pending Payment', icon: Clock, color: 'text-yellow-800 bg-yellow-100' },
  paid:      { label: 'Paid - Waiting to Ship', icon: Package, color: 'text-blue-800 bg-blue-100' },
  shipped:   { label: 'Shipped', icon: TruckIcon, color: 'text-purple-800 bg-purple-100' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-green-800 bg-green-100' },
  failed:    { label: 'Failed / Expired', icon: Clock, color: 'text-red-800 bg-red-100' },
};

export default function UserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, deleteAccount } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState('listings');
  
  const [wishlistItems, setWishlistItems] = useState([]);
  const [reminderAvailable, setReminderAvailable] = useState(false);
  const [appealDialog, setAppealDialog] = useState(false);
  const [appealForm, setAppealForm] = useState({ social_platforms: [], social_handle: '', follower_range: '' });
  const [appealAvailable, setAppealAvailable] = useState(false);
  const [appealing, setAppealing] = useState(false);
  const [address, setAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ full_name: '', phone: '', address_line: '', city: '', province: '', postal_code: '', country: 'Indonesia', latitude: null, longitude: null });
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [changePasswordStep, setChangePasswordStep] = useState('form'); // 'form' | 'verify'
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const isOwnProfile = user?.user_id === userId;

  useEffect(() => {
    let isInitialLoad = true;
    const fetchData = async () => {
      if (isInitialLoad) setLoading(true);
      try {
        const [profileRes, listingsRes] = await Promise.all([
          axios.get(`${API}/profiles/${userId}`),
          axios.get(`${API}/users/${userId}/listings`)
        ]);
        setProfileData(profileRes.data);
        setListings(listingsRes.data);

        // Check if reminder is available (24hr cooldown from last reminder or initial submission)
        const prof = profileRes.data?.profile;
        if (prof?.is_influencer && prof?.influencer_verification_status === 'pending_review') {
          const referenceTime = prof.last_reminder_sent || prof.verification_submitted_at;
          if (referenceTime) {
            const refDate = new Date(referenceTime);
            const hoursSince = (Date.now() - refDate.getTime()) / (1000 * 60 * 60);
            setReminderAvailable(hoursSince >= 24);
          } else {
            setReminderAvailable(false);
          }
        }
        // Check if appeal is available (rejected, rejection_count < 2, 24hr cooldown)
        if (prof?.is_influencer && prof?.influencer_verification_status === 'rejected') {
          const canAppeal = (prof.rejection_count || 0) < 2;
          if (canAppeal && prof.rejected_at) {
            const rejDate = new Date(prof.rejected_at);
            const hoursSince = (Date.now() - rejDate.getTime()) / (1000 * 60 * 60);
            setAppealAvailable(hoursSince >= 24);
          } else {
            setAppealAvailable(canAppeal);
          }
          setAppealForm({
            social_platforms: prof.social_platforms || [],
            social_handle: prof.social_handle || '',
            follower_range: prof.follower_range || '',
          });
        }

        if (isAuthenticated && !isOwnProfile) {
          try {
            const followRes = await axios.get(`${API}/follows/check/${userId}`);
            setIsFollowing(followRes.data.following);
          } catch { /* ignore */ }
        }
        if (isOwnProfile) {
          try {
            const ordersRes = await axios.get(`${API}/orders/me`);
            setOrders(ordersRes.data);
          } catch { /* ignore */ }
          try {
            const wishlistRes = await axios.get(`${API}/wishlist`);
            setWishlistItems(wishlistRes.data);
          } catch { /* ignore */ }
          try {
            const addrRes = await axios.get(`${API}/address/me`);
            if (addrRes.data?.full_name) {
              setAddress(addrRes.data);
              setAddressForm(addrRes.data);
            }
          } catch { /* ignore */ }
        } else {
          // For other users: fetch public wishlist if influencer
          try {
            const wishlistRes = await axios.get(`${API}/users/${userId}/wishlist`);
            setWishlistItems(wishlistRes.data);
          } catch { /* 403 = not influencer, expected */ }
        }
        // Fetch videos for influencer profiles
        if (profileRes.data?.profile?.is_influencer && profileRes.data?.profile?.influencer_verification_status === 'approved') {
          try {
            const videosRes = await axios.get(`${API}/users/${userId}/videos`);
            setVideos(videosRes.data);
          } catch { /* ignore */ }
        }
      } catch {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    isInitialLoad = false;
    const interval = setInterval(fetchData, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchData(); };
    const onFocus = () => fetchData();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [userId, isAuthenticated, isOwnProfile, navigate]);

  const handleFollow = async () => {
    if (!isAuthenticated) { setShowAuth(true); return; }
    try {
      const res = await axios.post(`${API}/follows/${userId}`, {});
      setIsFollowing(res.data.following);
      setProfileData(prev => ({ ...prev, followers_count: prev.followers_count + (res.data.following ? 1 : -1) }));
    } catch { toast.error('Failed to follow'); }
  };

  const handleSendReminder = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/profiles/send-verification-reminder`, {});
      toast.success(res.data.message);
      setReminderAvailable(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setVerifying(false);
    }
  };

  const getAppealCooldownText = () => {
    const prof = profileData?.profile;
    if (!prof?.rejected_at) return null;
    const rejDate = new Date(prof.rejected_at);
    const hoursSince = (Date.now() - rejDate.getTime()) / (1000 * 60 * 60);
    if (hoursSince >= 24) return null;
    const hoursLeft = Math.ceil(24 - hoursSince);
    return `You can request verification again in ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}`;
  };

  const handleAppealClick = () => {
    if (!appealAvailable) {
      const cooldownText = getAppealCooldownText();
      if (cooldownText) toast.info(cooldownText);
      return;
    }
    setAppealDialog(true);
  };

  const handleAppeal = async () => {
    setAppealing(true);
    try {
      const res = await axios.post(`${API}/profiles/request-reverification`, appealForm);
      toast.success(res.data.message);
      setAppealDialog(false);
      // Refresh profile
      const profileRes = await axios.get(`${API}/profiles/${userId}`);
      setProfileData(profileRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit appeal');
    } finally {
      setAppealing(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      toast.success('Account deleted');
      navigate('/');
    } catch {
      toast.error('Failed to delete account');
    }
  };

  

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v.video_id !== videoId));
      toast.success('Video deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete video');
    }
  };

  const handleOpenVideoPlayer = async (video) => {
    try {
      const res = await axios.get(`${API}/videos/${video.video_id}`);
      setSelectedVideo(res.data);
    } catch {
      setSelectedVideo(video);
    }
    setShowVideoPlayer(true);
  };

  const handleChangePasswordRequest = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password/request`, { new_password: newPassword, confirm_password: confirmPassword });
      toast.success('Verification code sent to your email');
      setChangePasswordStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangePasswordConfirm = async () => {
    if (!verifyCode || verifyCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password/confirm`, { code: verifyCode, new_password: newPassword });
      toast.success('Password changed successfully!');
      setChangePasswordDialog(false);
      setChangePasswordStep('form');
      setNewPassword('');
      setConfirmPassword('');
      setVerifyCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]"><Header />
        <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse">
          <div className="flex items-center gap-6 mb-8"><div className="w-24 h-24 rounded-full bg-cream-100" /><div className="space-y-3 flex-1"><div className="h-6 bg-cream-100 rounded w-48" /><div className="h-4 bg-cream-100 rounded w-32" /></div></div>
        </div>
      </div>
    );
  }
  if (!profileData) return null;
  const profile = profileData.profile;

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#78716C] hover:text-[#2C2A29] mb-6 transition-colors" data-testid="profile-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50 mb-8" data-testid="user-profile-header">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="relative">
              {profileData.picture ? (
                <img src={profileData.picture} alt={profileData.name} className="w-24 h-24 rounded-full object-cover border-4 border-blush-200" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-blush-200 flex items-center justify-center text-3xl font-heading font-bold text-[#2C2A29]">{profileData.name?.[0]}</div>
              )}
              {profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-blue-100 shadow-sm" data-testid="profile-verified-badge">
                  <BadgeCheck size={18} className="text-blue-500 fill-blue-50" />
                </div>
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29]">{profileData.name}</h1>
                {profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
                  <Badge className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold border border-blue-100 self-center flex items-center gap-1.5" data-testid="influencer-social-badge">
                    <BadgeCheck size={12} className="text-blue-500" />
                    {profile.social_platforms?.[0] && <span>{profile.social_platforms[0]}</span>}
                    {profile.social_handle && <span>· {profile.social_handle}</span>}
                    {profile.follower_range && <span>· {profile.follower_range}</span>}
                    {!profile.social_platforms?.[0] && !profile.social_handle && 'Verified Influencer'}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-sm font-body text-[#78716C] mb-3">
                {profile?.city && <span className="flex items-center gap-1"><MapPin size={14} />{profile.city}{profile.country ? `, ${profile.country}` : ''}</span>}
                {profile?.skin_type && <span className="flex items-center gap-1"><Droplets size={14} />{profile.skin_type} skin</span>}
              </div>
              {profile?.beauty_story && <p className="font-body text-sm text-[#78716C] italic mb-3" data-testid="beauty-story-display">"{profile.beauty_story}"</p>}
              <div className="flex items-center justify-center sm:justify-start gap-6 text-sm font-body mb-4">
                <div><span className="font-semibold text-[#2C2A29]">{profileData.followers_count || 0}</span> <span className="text-[#78716C]">followers</span></div>
                <div><span className="font-semibold text-[#2C2A29]">{profileData.following_count || 0}</span> <span className="text-[#78716C]">following</span></div>
                <div><span className="font-semibold text-[#2C2A29]">{listings.length}</span> <span className="text-[#78716C]">listings</span></div>
              </div>
              {profile?.skin_concerns?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4 justify-center sm:justify-start">
                  {profile.skin_concerns.map(c => <span key={c} className="bg-[#FFF5F5] text-[#F4C2C2] px-2.5 py-1 rounded-full text-xs font-medium">{c}</span>)}
                </div>
              )}
              {isOwnProfile ? (
                <>
                  <div className="flex flex-wrap items-center gap-3 justify-center sm:justify-start">
                    <button onClick={() => navigate('/complete-profile')}
                      className="border border-cream-100 bg-transparent text-[#2C2A29] rounded-full px-5 py-2 font-body text-sm font-semibold transition-all hover:border-[#F4C2C2] hover:bg-[#FFF5F5] flex items-center gap-2"
                      data-testid="edit-profile-btn"><Edit3 size={14} /> Edit Profile</button>
                    {/* Share button — visible only to verified influencers so they
                        can broadcast their Stashmart profile from social bios. */}
                    {profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
                      <ShareButton
                        url={typeof window !== 'undefined' ? `${window.location.origin}/profile/${profile?.user_id || userId}` : ''}
                        title={`${profile?.name || 'Creator'} on {Beauty} Stashmart`}
                        text={`Follow ${profile?.name || 'this creator'} on {Beauty} Stashmart for beauty product picks and reviews:`}
                        label="Share profile"
                        variant="ghost"
                        testid="share-profile-btn"
                      />
                    )}
                    {profile?.is_influencer && profile?.influencer_verification_status === 'pending_review' && (
                      <>
                        <span className="flex items-center gap-1.5 text-xs font-body text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full" data-testid="pending-badge">
                          <Clock size={12} /> Pending Verification
                        </span>
                        {!profile?.rejection_count && !profile?.rejected_at && !profile?.is_appeal && (
                          <button onClick={handleSendReminder} disabled={verifying || !reminderAvailable}
                            className="border border-amber-200 text-amber-600 rounded-full px-4 py-2 font-body text-xs font-semibold transition-all hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                            data-testid="send-reminder-btn"
                            title={!reminderAvailable ? 'Available after 24 hours' : ''}>
                            <Bell size={12} /> {verifying ? 'Sending...' : 'Remind to Verify'}
                          </button>
                        )}
                      </>
                    )}
                    {profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
                      <span className="flex items-center gap-1.5 text-xs font-body text-green-600 bg-green-50 px-3 py-1.5 rounded-full" data-testid="verified-badge">
                        <ShieldCheck size={12} /> {profile?.is_demo ? 'Demo Influencer' : 'Verified Influencer'}
                      </span>
                    )}
                    {profile?.is_influencer && profile?.influencer_verification_status === 'rejected' && (
                      <>
                        <span className="flex items-center gap-1.5 text-xs font-body text-red-500 bg-red-50 px-3 py-1.5 rounded-full" data-testid="rejected-badge">
                          <ShieldX size={12} /> Verification Rejected
                        </span>
                        {!profile?.is_appeal ? (
                          <button onClick={handleAppealClick}
                            className={`border border-amber-200 text-amber-600 rounded-full px-4 py-2 font-body text-xs font-semibold transition-all hover:bg-amber-50 flex items-center gap-1.5 ${!appealAvailable ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid="appeal-btn"
                            title={!appealAvailable ? (getAppealCooldownText() || 'Available after 24 hours') : 'Fix your details and request again'}>
                            <RefreshCw size={12} /> Request Again
                          </button>
                        ) : (
                          <span className="text-xs font-body text-[#78716C] bg-cream-50 px-3 py-1.5 rounded-full" data-testid="permanent-reject-badge">
                            Permanently rejected
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {profile?.is_influencer && profile?.influencer_verification_status === 'pending_review' && (
                    <div className="mt-4 bg-amber-50/80 border border-amber-200 rounded-2xl p-4 sm:p-5" data-testid="verification-code-box">
                      <div className="flex items-center gap-2 mb-3">
                        <KeyRound size={16} className="text-amber-600" />
                        <h3 className="text-sm font-body font-semibold text-amber-800">Verification Secret Code</h3>
                      </div>
                      {profile?.verification_code && (!profile?.verification_code_expires_at || new Date(profile.verification_code_expires_at) > new Date()) ? (
                        <>
                          <div className="flex items-center gap-3 mb-3">
                            <code className="bg-white border border-amber-200 rounded-xl px-4 py-2.5 font-mono text-lg font-bold text-amber-900 tracking-[0.25em] select-all" data-testid="verification-code-value">
                              {profile.verification_code}
                            </code>
                            <button
                              onClick={() => { navigator.clipboard.writeText(profile.verification_code); toast.success('Code copied!'); }}
                              className="text-amber-600 hover:text-amber-800 transition-colors p-1.5 rounded-lg hover:bg-amber-100"
                              data-testid="copy-code-btn"
                              title="Copy code"
                            >
                              <Copy size={16} />
                            </button>
                          </div>
                          <p className="text-xs font-body text-amber-700 leading-relaxed mb-1.5">
                            This code is valid for <span className="font-semibold">24 hours</span>. We'll send a DM to your linked {profile.social_platforms?.[0] || 'social'} account ({profile.social_handle || 'your handle'}) asking for this code to verify your identity.
                          </p>
                          <p className="text-xs font-body text-amber-600">
                            You can still buy and list products on Stashmart while waiting.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs font-body text-amber-700">
                          Your verification code has expired. Please request verification again from your profile settings.
                        </p>
                      )}
                    </div>
                  )}
                  {profile?.is_influencer && profile?.influencer_verification_status === 'rejected' && profile?.rejected_at && (Date.now() - new Date(profile.rejected_at).getTime()) < 30 * 24 * 60 * 60 * 1000 && (
                    <div className="text-xs font-body bg-red-50/50 rounded-xl px-4 py-2.5 mt-3 text-center sm:text-left" data-testid="rejected-reassurance">
                      {profile?.rejection_reason === 'content_not_relevant' ? (
                        <p className="text-red-600">Verification rejected: Your content isn't focused enough on beauty.<br />You can still buy & sell, and request verification again after 24 hours.</p>
                      ) : profile?.rejection_reason === 'wrong_expired_code' ? (
                        <p className="text-red-600">Verification rejected: Secret code is not valid.<br />You can still buy & sell, and request verification again after 24 hours.</p>
                      ) : profile?.rejection_reason === 'account_private' ? (
                        <p className="text-red-600">Verification rejected: account is private.<br />You can still buy & sell, and request verification again after 24 hours.</p>
                      ) : profile?.rejection_reason?.startsWith('other:') ? (
                        <p className="text-red-600">Verification rejected: {profile.rejection_reason.slice(6)}.<br />You can still buy & sell, and request verification again after 24 hours.</p>
                      ) : (
                        <p className="text-red-600">Verification rejected.<br />You can still buy & sell, and request verification again after 24 hours.</p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start">
                  <button onClick={handleFollow}
                    className={`rounded-full px-5 py-2 font-body text-sm font-semibold transition-all flex items-center gap-2 ${
                      isFollowing ? 'bg-cream-100 text-[#2C2A29] hover:bg-cream-200' : 'bg-[#F4C2C2] text-[#2C2A29] hover:bg-[#ECAEAE] hover:shadow-md'}`}
                    data-testid="follow-profile-btn">
                    {isFollowing ? <><UserCheck size={14} /> Following</> : <><UserPlus size={14} /> Follow</>}
                  </button>
                  {profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
                    <ShareButton
                      url={typeof window !== 'undefined' ? `${window.location.origin}/profile/${profile?.user_id || userId}` : ''}
                      title={`${profile?.name || 'Creator'} on {Beauty} Stashmart`}
                      text={`Follow ${profile?.name || 'this creator'} on {Beauty} Stashmart for beauty product picks and reviews:`}
                      label="Share profile"
                      variant="ghost"
                      testid="share-profile-btn"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs (own profile) */}
        {isOwnProfile && (
          <div className="flex gap-1 mb-6 bg-cream-50 rounded-full p-1 w-fit flex-wrap" data-testid="profile-tabs">
            {[
              'listings',
              ...(profile?.is_influencer && profile?.influencer_verification_status === 'approved' ? ['videos'] : []),
              'wishlist', 'purchases', 'address'
            ].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all capitalize ${
                  activeTab === tab ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
                data-testid={`tab-${tab}`}>
                {tab === 'purchases' ? 'Purchase History' : tab === 'wishlist' ? `Wishlist (${wishlistItems.length})` : tab === 'videos' ? `Videos (${videos.length})` : tab === 'address' ? 'Address' : tab}
              </button>
            ))}
          </div>
        )}

        {/* Tabs (other user's profile - influencer with videos/wishlist) */}
        {!isOwnProfile && profile?.is_influencer && profile?.influencer_verification_status === 'approved' && (
          <div className="flex gap-1 mb-6 bg-cream-50 rounded-full p-1 w-fit flex-wrap" data-testid="profile-public-tabs">
            {['listings', ...(videos.length > 0 ? ['videos'] : []), ...(wishlistItems.length > 0 ? ['wishlist'] : [])].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all capitalize ${
                  activeTab === tab ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
                data-testid={`tab-${tab}`}>
                {tab === 'videos' ? `Videos (${videos.length})` : tab === 'wishlist' ? `Wishlist (${wishlistItems.length})` : tab}
              </button>
            ))}
          </div>
        )}

        {/* Listings Tab */}
        {(activeTab === 'listings') && (
          <div>
            <h2 className="font-heading text-2xl text-[#2C2A29] mb-6">
              {isOwnProfile ? 'Your Listings' : `${profileData.name?.split(' ')[0]}'s Listings`}
            </h2>
            {listings.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-cream-100/50">
                <p className="font-body text-[#78716C]">No active listings</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="user-listings-grid">
                {listings.map(listing => (
                  <div key={listing.listing_id} className="relative">
                    <ProductCard listing={listing} />
                    
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl text-[#2C2A29] flex items-center gap-2">
                <Film size={22} className="text-[#D48585]" /> {isOwnProfile ? 'Your Videos' : 'Videos'}
              </h2>
              {isOwnProfile && (
                <button onClick={() => navigate('/post-video')}
                  className="text-sm font-body font-semibold text-[#D48585] hover:text-[#2C2A29] transition-colors flex items-center gap-1"
                  data-testid="profile-post-video-btn">
                  <Film size={14} /> Post New
                </button>
              )}
            </div>
            {videos.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-cream-100/50">
                <Film size={40} className="mx-auto text-cream-200 mb-3" />
                <p className="font-body text-[#78716C]">No videos posted yet</p>
                {isOwnProfile && (
                  <button onClick={() => navigate('/post-video')}
                    className="mt-4 bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-2.5 font-body font-semibold hover:bg-[#ECAEAE] transition-all"
                    data-testid="profile-first-video-btn">
                    Post Your First Video
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="profile-videos-grid">
                {videos.map(video => (
                  <div key={video.video_id}
                    onClick={() => handleOpenVideoPlayer(video)}
                    className="cursor-pointer group relative"
                    data-testid={`profile-video-${video.video_id}`}>
                    <div className="aspect-[9/12] rounded-2xl overflow-hidden bg-[#2C2A29]">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2C2A29] to-[#1a1918]">
                          <Film size={32} className="text-[#78716C]" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors rounded-2xl">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                          <Play size={16} className="text-[#2C2A29] ml-0.5" fill="#2C2A29" />
                        </div>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="bg-black/50 backdrop-blur-sm text-white px-2 py-0.5 rounded-full text-[10px] font-body font-semibold">
                          {video.category}
                        </span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2">
                        <span className="flex items-center gap-1 text-white text-[11px] font-body">
                          <Heart size={10} />{video.likes_count || 0}
                        </span>
                        <span className="flex items-center gap-1 text-white text-[11px] font-body">
                          <Eye size={10} />{video.views_count || 0}
                        </span>
                      </div>
                    </div>
                    <p className="font-body text-xs font-medium text-[#2C2A29] mt-2 line-clamp-2 leading-snug">{video.title}</p>
                    {isOwnProfile && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video.video_id); }}
                        className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`delete-video-${video.video_id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Wishlist Tab */}
        {/* Wishlist Tab - Own profile or public influencer profile */}
        {activeTab === 'wishlist' && (isOwnProfile || (profile?.is_influencer && profile?.influencer_verification_status === 'approved')) && (
          <div>
            <h2 className="font-heading text-2xl text-[#2C2A29] mb-6 flex items-center gap-2">
              <Heart size={22} className="text-[#E05A5A]" /> {isOwnProfile ? 'Your Wishlist' : `${profile?.display_name || 'Their'} Wishlist`}
            </h2>
            {wishlistItems.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-cream-100/50">
                <Heart size={40} className="mx-auto text-cream-200 mb-3" />
                <p className="font-body text-[#78716C]">{isOwnProfile ? 'Your wishlist is empty' : 'No wishlisted items yet'}</p>
                {isOwnProfile && <p className="font-body text-sm text-[#A89F91] mt-1">Heart items to save them here</p>}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" data-testid="wishlist-grid">
                {wishlistItems.map(item => (
                  <ProductCard key={item.listing_id} listing={item} onWishlistChange={isOwnProfile ? (id) => setWishlistItems(prev => prev.filter(l => l.listing_id !== id)) : undefined} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Purchase History Tab */}
        {isOwnProfile && activeTab === 'purchases' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl text-[#2C2A29]">Purchase History</h2>
              <button onClick={() => navigate('/orders')} className="text-sm font-body font-semibold text-[#C5A880] hover:text-[#2C2A29] transition-colors">
                View All Orders &rarr;
              </button>
            </div>
            {orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-cream-100/50">
                <p className="font-body text-[#78716C]">No purchases yet</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="purchase-history">
                {orders.map(order => {
                  const status = order.payment_status || order.status || 'pending';
                  const mappedStatus = status === 'waiting_for_shipment' ? 'paid' : status;
                  const sc = STATUS_CONFIG[mappedStatus] || STATUS_CONFIG.pending;
                  const StatusIcon = sc.icon;
                  const displayAmount = order.amount || order.total || 0;
                  const photo = order.photo || (order.items && order.items[0]?.photo);
                  const name = order.product_name || (order.items && order.items[0]?.product_name) || 'Order';
                  const brand = order.brand || (order.items && order.items[0]?.brand) || '';
                  return (
                    <div key={order.order_id} className="bg-white rounded-2xl p-5 border border-cream-100/50 cursor-pointer hover:border-[#F4C2C2]/50 transition-colors" onClick={() => navigate('/orders')} data-testid={`order-${order.order_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-body text-[#A89F91]">{new Date(order.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-medium ${sc.color}`}>
                          <StatusIcon size={12} />{sc.label}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        {photo && <img src={photo} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-body text-[#A89F91]">{brand}</p>
                          <p className="font-body text-sm font-medium text-[#2C2A29] truncate">{name}</p>
                          <p className="text-xs font-body text-[#78716C]">Qty: {order.quantity || 1}</p>
                        </div>
                        <span className="font-heading font-semibold text-[#2C2A29]">{displayAmount ? formatPrice(displayAmount) : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Address Tab */}
        {isOwnProfile && activeTab === 'address' && (
          <div>
            <h2 className="font-heading text-2xl text-[#2C2A29] mb-6 flex items-center gap-2">
              <MapPin size={22} className="text-[#C5A880]" /> Delivery Address
            </h2>
            {address ? (
              <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="profile-address-card">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-body font-semibold text-[#2C2A29]" data-testid="profile-address-name">{address.full_name}</p>
                    <p className="font-body text-sm text-[#78716C]">{address.phone}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setAddressForm(address); setShowAddressForm(true); }}
                      className="text-xs font-body font-semibold text-[#C5A880] hover:text-[#2C2A29] transition-colors flex items-center gap-1"
                      data-testid="profile-edit-address-btn">
                      <Edit3 size={12} /> Edit
                    </button>
                    <button onClick={async () => {
                        if (!window.confirm('Delete this address?')) return;
                        try {
                          await axios.delete(`${API}/address/me`);
                          setAddress(null);
                          setAddressForm({ full_name: '', phone: '', address_line: '', city: '', province: '', postal_code: '', country: 'Indonesia', latitude: null, longitude: null });
                          toast.success('Address deleted');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to delete address');
                        }
                      }}
                      className="text-xs font-body font-semibold text-red-400 hover:text-red-600 transition-colors flex items-center gap-1"
                      data-testid="profile-delete-address-btn">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                <p className="font-body text-sm text-[#2C2A29]" data-testid="profile-address-line">{address.address_line}</p>
                <p className="font-body text-sm text-[#78716C] mt-0.5">{address.city}, {address.province} {address.postal_code}</p>
                {address.latitude && address.longitude && (
                  <p className="text-xs font-body text-green-600 mt-2 flex items-center gap-1" data-testid="profile-address-pinned">
                    <Navigation size={10} /> Location pinned
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-3xl border border-cream-100/50" data-testid="profile-no-address">
                <MapPin size={40} className="mx-auto text-cream-200 mb-3" />
                <p className="font-body text-[#78716C]">No delivery address saved</p>
                <p className="font-body text-sm text-[#A89F91] mt-1 mb-4">Add your address for faster checkout</p>
                <button onClick={() => setShowAddressForm(true)}
                  className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-2.5 font-body font-semibold hover:bg-[#ECAEAE] transition-all"
                  data-testid="profile-add-address-btn">
                  Add Address
                </button>
              </div>
            )}
            <AddressFormDialog
              open={showAddressForm}
              onOpenChange={setShowAddressForm}
              addressForm={addressForm}
              setAddressForm={setAddressForm}
              onSave={async (e) => {
                e.preventDefault();
                try {
                  const res = await axios.put(`${API}/address/me`, addressForm);
                  setAddress(res.data);
                  setShowAddressForm(false);
                  toast.success('Address saved');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to save address');
                }
              }}
            />
          </div>
        )}

        {/* Account Settings (own profile) */}
        {isOwnProfile && (
          <div className="mt-16 pt-6 border-t border-cream-100/30 flex items-center gap-6">
            {user?.auth_type !== 'google' && (
              <button onClick={() => { setChangePasswordDialog(true); setChangePasswordStep('form'); setNewPassword(''); setConfirmPassword(''); setVerifyCode(''); setShowNewPassword(false); }}
                className="text-xs font-body text-[#78716C] hover:text-[#2C2A29] transition-colors flex items-center gap-1.5"
                data-testid="change-password-btn">
                <Lock size={12} /> Change password
              </button>
            )}
            <button onClick={() => setShowDeleteConfirm(true)}
              className="text-xs font-body text-[#A89F91] hover:text-red-400 transition-colors"
              data-testid="delete-account-btn">
              Delete account
            </button>
          </div>
        )}
      </main>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4 text-center" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Delete Account?</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Are you sure you want to delete your account? Once deleted, your account can't be recovered.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <button onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="delete-cancel-btn">No</button>
            <button onClick={handleDeleteAccount}
              className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all"
              data-testid="delete-confirm-btn">Yes, Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog} onOpenChange={(v) => { if (!v) { setChangePasswordDialog(false); setChangePasswordStep('form'); setNewPassword(''); setConfirmPassword(''); setVerifyCode(''); } }}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Change Password</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              {changePasswordStep === 'form'
                ? 'Enter your new password. We\'ll send a code to verify.'
                : `Enter the 6-digit code sent to ${user?.email}`}
            </DialogDescription>
          </DialogHeader>

          {changePasswordStep === 'form' ? (
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'} value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 pr-10 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89F91] hover:text-[#2C2A29]"
                    data-testid="toggle-new-password">
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'} value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 pr-10 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                    data-testid="confirm-password-input"
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89F91] hover:text-[#2C2A29]"
                    data-testid="toggle-confirm-password">
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setChangePasswordDialog(false)}
                  className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
                <button onClick={handleChangePasswordRequest} disabled={changingPassword || !newPassword || !confirmPassword}
                  className="flex-1 bg-[#2C2A29] text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-[#1a1918] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="change-password-send-code-btn">
                  {changingPassword ? 'Confirming...' : 'Confirm'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Verification Code</label>
                <input
                  type="text" value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-lg text-center text-[#2C2A29] tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  maxLength={6}
                  data-testid="change-password-code-input"
                />
              </div>
              <p className="text-xs font-body text-[#A89F91]">Code expires in 10 minutes.</p>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setChangePasswordStep('form')}
                  className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Back</button>
                <button onClick={handleChangePasswordConfirm} disabled={changingPassword || verifyCode.length !== 6}
                  className="flex-1 bg-[#2C2A29] text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-[#1a1918] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="change-password-confirm-btn">
                  {changingPassword ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appeal Dialog */}
      <Dialog open={appealDialog} onOpenChange={setAppealDialog}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="appeal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Request Reverification</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Update your details and submit for another review. You have one appeal attempt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Platform</label>
              <select
                value={appealForm.social_platforms[0] || ''}
                onChange={e => setAppealForm(f => ({ ...f, social_platforms: [e.target.value] }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200"
                data-testid="appeal-platform">
                <option value="">Select platform</option>
                {['Instagram', 'TikTok', 'YouTube', 'Twitter', 'Facebook'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Social Handle</label>
              <input
                type="text" value={appealForm.social_handle}
                onChange={e => setAppealForm(f => ({ ...f, social_handle: e.target.value }))}
                placeholder="@yourhandle"
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200"
                data-testid="appeal-handle"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Follower Range</label>
              <select
                value={appealForm.follower_range}
                onChange={e => setAppealForm(f => ({ ...f, follower_range: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200"
                data-testid="appeal-follower-range">
                <option value="">Select range</option>
                {['1K - 10K (Nano)', '10K - 50K (Micro)', '50K - 100K (Mid-tier)', '100K - 500K (Macro)', '500K+ (Mega)'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setAppealDialog(false)}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
              <button onClick={handleAppeal} disabled={appealing || !appealForm.social_platforms[0] || !appealForm.social_handle || !appealForm.follower_range}
                className="flex-1 bg-[#C5A880] text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-[#B89970] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="appeal-submit-btn">
                <RefreshCw size={14} className={appealing ? 'animate-spin' : ''} />
                {appealing ? 'Submitting...' : 'Submit Appeal'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      

      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
      <VideoPlayerModal
        video={selectedVideo}
        open={showVideoPlayer}
        onOpenChange={(open) => { setShowVideoPlayer(open); if (!open) setSelectedVideo(null); }}
        onAuthRequired={() => setShowAuth(true)}
        onVideoDeleted={(id) => { setVideos(prev => prev.filter(v => v.video_id !== id)); setShowVideoPlayer(false); setSelectedVideo(null); }}
        onVideoUpdated={(id, updates) => { setVideos(prev => prev.map(v => v.video_id === id ? { ...v, ...updates } : v)); setSelectedVideo(prev => prev ? { ...prev, ...updates } : prev); }}
      />
    </div>
  );
}
